import {isNil, isNotNil} from 'ramda';

import type {CustomerMessageResponseBody} from '@dms/api/core';
import type {BusinessCaseActivityResponseBody} from '@dms/api/sales';

import {
  ASSISTANT_BUSINESS_CASE_EXCERPT_MAX_EVENTS,
  ASSISTANT_INTEREST_TEXT_MAX_CHARACTERS,
  type AssistantBusinessCaseContext,
} from '@prompt-bridge/shared/ai-assistant-core';

import {compareBusinessCaseTimelineEventsNewestFirst} from './compareBusinessCaseTimelineEventsNewestFirst';
import {mapBusinessCaseActivityToTimelineEvent} from './mapBusinessCaseActivityToTimelineEvent';
import {
  type AssistantBusinessCaseExcerpt,
  toAssistantBusinessCaseExcerpt,
} from './toAssistantBusinessCaseExcerpt';
import {toBoundedAssistantBusinessCaseText} from './toBoundedAssistantBusinessCaseText';
import {toBusinessCaseMessageTimelineEvent} from './toBusinessCaseMessageTimelineEvent';

export type AssistantBusinessCaseTimelineResult = {
  timeline: AssistantBusinessCaseContext['timeline'];
  omittedUnmappedEventCount: number;
};

type BusinessCaseTimelineActivity = Pick<
  BusinessCaseActivityResponseBody,
  'activityType' | 'description' | 'id' | 'relatedCall' | 'relatedMessage' | 'subject' | 'timestamp'
>;
type BusinessCaseTimelineMessage = Pick<
  CustomerMessageResponseBody,
  'call' | 'channel' | 'content' | 'createdAt' | 'direction' | 'id' | 'subject'
>;

const INBOUND_COMMUNICATION_KINDS: ReadonlyArray<
  AssistantBusinessCaseContext['timeline'][number]['kind']
> = ['message', 'call', 'email', 'sms'];

export const toAssistantBusinessCaseTimelineResult = (
  activities: readonly BusinessCaseTimelineActivity[],
  customerMessages: readonly BusinessCaseTimelineMessage[]
): AssistantBusinessCaseTimelineResult => {
  const activityMessageIds = new Set(
    activities.flatMap((activity) =>
      isNotNil(activity.relatedMessage) ? [activity.relatedMessage.id] : []
    )
  );
  const activityCallIds = new Set(
    activities.flatMap((activity) =>
      isNotNil(activity.relatedCall) ? [activity.relatedCall.id] : []
    )
  );
  let omittedUnmappedEventCount = 0;
  const activityEvents = activities.flatMap((activity) => {
    const mapping = mapBusinessCaseActivityToTimelineEvent(activity.activityType);
    if (isNil(mapping)) {
      omittedUnmappedEventCount += 1;
      return [];
    }

    const subject = toBoundedAssistantBusinessCaseText(
      activity.relatedMessage?.subject ?? activity.subject ?? activity.description
    );
    return [
      {
        id: activity.id.slice(0, ASSISTANT_INTEREST_TEXT_MAX_CHARACTERS),
        occurredAt: activity.timestamp,
        ...mapping,
        ...(isNotNil(subject) ? {subject} : {}),
        body: activity.relatedMessage?.body ?? activity.description,
      },
    ];
  });
  const messageEvents = customerMessages
    .filter(
      (message) =>
        !activityMessageIds.has(message.id) &&
        (isNil(message.call) || !activityCallIds.has(message.call.id))
    )
    .map((message) => {
      const subject = toBoundedAssistantBusinessCaseText(message.subject);
      return {
        ...toBusinessCaseMessageTimelineEvent(message),
        ...(isNotNil(subject) ? {subject} : {}),
        body: message.content,
      };
    });
  const sortedEvents = [...activityEvents, ...messageEvents].sort(
    compareBusinessCaseTimelineEventsNewestFirst
  );
  const excerptsByEventIndex = new Map<number, AssistantBusinessCaseExcerpt>();
  const guaranteedExcerptEventIndex = sortedEvents.findIndex(({body, ...event}, eventIndex) => {
    if (
      isNil(body) ||
      event.direction !== 'inbound' ||
      !INBOUND_COMMUNICATION_KINDS.includes(event.kind)
    ) {
      return false;
    }

    const excerpt = toAssistantBusinessCaseExcerpt(body);
    if (isNil(excerpt)) {
      return false;
    }

    excerptsByEventIndex.set(eventIndex, excerpt);
    return true;
  });

  sortedEvents.forEach(({body}, eventIndex) => {
    if (
      isNil(body) ||
      eventIndex === guaranteedExcerptEventIndex ||
      excerptsByEventIndex.size >= ASSISTANT_BUSINESS_CASE_EXCERPT_MAX_EVENTS
    ) {
      return;
    }

    const excerpt = toAssistantBusinessCaseExcerpt(body);
    if (isNotNil(excerpt)) {
      excerptsByEventIndex.set(eventIndex, excerpt);
    }
  });

  const timeline = sortedEvents.map(({body: _body, ...event}, eventIndex) => {
    const excerpt = excerptsByEventIndex.get(eventIndex);

    return {
      ...event,
      ...(isNotNil(excerpt) ? {excerpt: excerpt.text, excerptTruncated: excerpt.truncated} : {}),
    };
  });

  return {
    timeline,
    omittedUnmappedEventCount,
  };
};

export const toAssistantBusinessCaseTimeline = (
  activities: readonly BusinessCaseTimelineActivity[],
  customerMessages: readonly BusinessCaseTimelineMessage[]
): AssistantBusinessCaseContext['timeline'] =>
  toAssistantBusinessCaseTimelineResult(activities, customerMessages).timeline;
